import { FC, useEffect, useMemo } from 'react';

import { GiftDeliveryZoneManager } from '@service/giftSeller/components/GiftDeliveryZoneManager';
import { listGiftItemBrand } from '@shared/api/giftItemBrand/giftItemBrand.controller';
import {
  GiftItemBrand,
  SettlementInfo,
} from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { useAsyncFn } from 'react-use';

// 정산정보: 관리자가 설정한 브랜드별 정산 단가를 조회만 한다 (수정은 관리자 상품관리에서만).
// - 배송관리자(gift-brand-seller): 백엔드 owningFilter로 본인 브랜드 1건만 내려온다.
// - 관리자(admin/GiftAgent): 전체 브랜드를 카드로 나열.

const won = (v: number) => `${v.toLocaleString()}원`;
const isSet = (v?: number): v is number => typeof v === 'number';
const isPositive = (v?: number): v is number => isSet(v) && v > 0;

// 정산정보가 하나라도 채워진 브랜드인지
const hasSettlement = (brand: GiftItemBrand) => {
  const si = brand.settlementInfo;
  return (
    si != null &&
    (si.deliveryFeeMode != null ||
      si.shippingFee != null ||
      si.freeShippingMinQuantity != null ||
      si.courierFee != null ||
      si.courierFreeMinQuantity != null ||
      si.settlementDay != null)
  );
};

// 카드로 보여줄 내용이 있는 브랜드인지 (정산정보 또는 배송 정책)
const hasContent = (brand: GiftItemBrand) =>
  hasSettlement(brand) || brand.deliveryDetail?.zonePolicy != null;

// "N개 미만 주문에만 적용" — 기준 수량이 없으면 조건 없이 항상 적용
const quantityHint = (minQuantity: number | undefined, always: string) =>
  isPositive(minQuantity)
    ? `주문 수량 ${minQuantity.toLocaleString()}개 미만일 때만 적용 · ${minQuantity.toLocaleString()}개 이상은 없음`
    : always;

const InfoRow: FC<{ label: string; value: string; hint?: string }> = ({
  label,
  value,
  hint,
}) => (
  <div className="flex items-start justify-between gap-4 py-2.5">
    <p className="shrink-0 text-sm text-gray-600">{label}</p>
    <div className="text-right">
      <p className="text-sm font-semibold text-gray-900">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
    </div>
  </div>
);

const SettlementCard: FC<{ brand: GiftItemBrand; isAdmin: boolean }> = ({
  brand,
  isAdmin,
}) => {
  const si: SettlementInfo = brand.settlementInfo ?? {};
  const empty = !hasSettlement(brand);
  const mode = si.deliveryFeeMode ?? 'quantity';
  const policy = brand.deliveryDetail?.zonePolicy;

  return (
    <div className="rounded-lg bg-white p-5 shadow">
      <h3 className="mb-3 text-base font-semibold">{brand.name}</h3>
      {empty ? (
        <p className="rounded-md bg-gray-50 p-3 text-sm text-gray-500">
          아직 등록된 정산정보가 없습니다. 관리자에게 문의해주세요.
        </p>
      ) : (
        // 설정된 항목만 노출한다 (미설정·0원 항목은 업체가 볼 필요가 없다)
        <div className="divide-y divide-gray-100">
          {isSet(si.settlementDay) && (
            <InfoRow label="정산일" value={`익월 ${si.settlementDay}일`} />
          )}
          <InfoRow
            label="배송비 정산"
            value={
              mode === 'orderDeliveryFee'
                ? '고객이 결제한 배송비 전액 지급'
                : mode === 'included'
                ? '별도 지급 없음 — 상품 단가에 포함'
                : '수량 기준 택배비 지급'
            }
            hint={
              mode === 'orderDeliveryFee'
                ? '기본배송비 + 지역 추가배송비를 주문당 1회 지급'
                : undefined
            }
          />
          {mode === 'quantity' && isPositive(si.courierFee) && (
            <InfoRow
              label="택배비 (주문 건당)"
              value={won(si.courierFee)}
              hint={quantityHint(si.courierFreeMinQuantity, '모든 주문에 적용')}
            />
          )}
          {isPositive(si.shippingFee) && (
            <InfoRow
              label="소량주문 추가단가 (개당)"
              value={won(si.shippingFee)}
              hint={quantityHint(si.freeShippingMinQuantity, '모든 주문에 적용')}
            />
          )}
        </div>
      )}
      {policy && (
        <section className="mt-5 space-y-4 border-t pt-4">
          <h4 className="font-semibold">고객 배송비 · 지역별 배송 정책</h4>
          <p className="text-sm text-gray-600">
            {policy.enabled ? '지역별 배송비 사용 중' : '지역별 배송비 사용 안 함'}.
            아래는 고객에게 청구하는 배송비 기준입니다. 업체 지급 여부는 위 정산 방식에
            따릅니다.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr>
                  {['적용 시도', '기본배송비', '기본배송비 면제금액'].map((h) => (
                    <th key={h} className="border-b p-2">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {policy.baseFeeRules?.map((rule, index) => (
                  <tr key={index}>
                    <td className="border-b p-2">{rule.sidoIn.join(', ')}</td>
                    <td className="border-b p-2">{won(rule.fee)}</td>
                    <td className="border-b p-2">{won(rule.freeOverAmount)} 이상</td>
                  </tr>
                ))}
                <tr>
                  <td className="border-b p-2">그 외 시도 (기본값)</td>
                  <td className="border-b p-2">{won(policy.defaultBaseFee?.fee ?? 0)}</td>
                  <td className="border-b p-2">
                    {won(policy.defaultBaseFee?.freeOverAmount ?? 0)} 이상
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500">
            시도 규칙은 위에서부터 처음 일치하는 기준을 적용합니다. 지역 추가배송비는
            기본배송비 면제 여부와 관계없이 붙습니다. 미등록 지역:{' '}
            {policy.unlistedZone === 'allow' ? '배송가능 · 추가배송비 0원' : '배송불가'}.
          </p>
          {policy.enabled && (
            <GiftDeliveryZoneManager
              brandId={brand._id}
              getPolicy={() => policy}
              policyRevision={JSON.stringify(policy)}
              readOnly
              brandSeller={!isAdmin}
            />
          )}
        </section>
      )}
    </div>
  );
};

export const GiftBrandSettlementInfoPage: FC = function GiftBrandSettlementInfoPage() {
  const { giftBrandRole } = useAuth();
  const role = giftBrandRole() as LowerRole.Admin | LowerRole.GiftBrandSeller;
  const isAdmin = role === LowerRole.Admin;

  const [{ loading, error, value: brands }, fetchBrands] = useAsyncFn(
    async () => listGiftItemBrand(role),
    [role],
  );

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  // 볼 내용이 있는 브랜드만 카드로. 미설정 브랜드는 관리자에게만 하단에 이름으로 모아 보여준다.
  const { rows, unsetBrands } = useMemo(() => {
    const all = brands ?? [];
    // 배송관리자는 본인 브랜드 1건이라 미설정이어도 안내 카드를 그대로 보여준다.
    if (!isAdmin) return { rows: all, unsetBrands: [] as GiftItemBrand[] };
    return {
      rows: all.filter(hasContent),
      unsetBrands: all.filter((brand) => !hasContent(brand)),
    };
  }, [brands, isAdmin]);

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-bold">정산정보</h2>
        <p className="mt-1 text-sm text-gray-500">
          관리자가 설정한 브랜드 정산 단가를 확인하세요.
        </p>
      </div>

      <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-800">
        정산정보는 조회 전용입니다. 변경이 필요하면 관리자에게 문의해주세요.
        {isAdmin && (
          <span className="mt-1 block text-indigo-700">
            관리자는 상품관리 &gt; 브랜드 수정 화면에서 정산정보를 변경할 수 있습니다.
          </span>
        )}
      </div>

      {loading ? (
        <div className="center-box py-16">
          <Loading />
        </div>
      ) : error ? (
        <div role="alert" className="rounded bg-red-50 p-4 text-sm text-red-700">
          정산정보를 불러오지 못했습니다.{' '}
          <button type="button" onClick={() => void fetchBrands()}>
            다시 시도
          </button>
        </div>
      ) : rows.length === 0 && unsetBrands.length === 0 ? (
        <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
          조회할 정산정보가 없습니다.
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((brand) => (
            <SettlementCard key={brand._id} brand={brand} isAdmin={isAdmin} />
          ))}
          {unsetBrands.length > 0 && (
            <details className="rounded-lg bg-white p-4 text-sm text-gray-600 shadow">
              <summary className="cursor-pointer font-medium">
                정산정보 미설정 브랜드 {unsetBrands.length}곳
              </summary>
              <p className="mt-2 text-gray-500">
                {unsetBrands.map((brand) => brand.name).join(', ')}
              </p>
            </details>
          )}
        </div>
      )}
    </div>
  );
};
